/**
 * What outreach is allowed to cost, how fast it is allowed to go, and where the
 * bands that decide who is worth an email are drawn. Shared by the api, the
 * worker and the interface, so the number that refuses a send and the number
 * the screen reports cannot disagree.
 */

/**
 * How many a site may send in a day, follow-ups included. Ten is the ceiling
 * rather than a target: a sending domain that mails more strangers than that
 * reads as a campaign, and the default is the first week of a warm-up.
 */
export const OUTREACH_DEFAULT_DAILY_CAP = 3
export const OUTREACH_MAX_DAILY_CAP = 10

/**
 * How many drafts are kept waiting on a person. Research is pulled to this
 * rather than pushed over a backlog: a model reading four hundred domains a
 * human will never reach is spend with no reader, and a queue a little longer
 * than a day's sending is enough that dismissing a few does not leave the day
 * short.
 */
export const OUTREACH_READY_TARGET = 12

/**
 * A ceiling on reads per site per day, so a run of dismissals cannot turn the
 * pull into a loop over the whole pool. Set from the first live fill: most reads
 * end at a free gate, and a cold queue took over eighty to reach a dozen drafts.
 */
export const OUTREACH_RESEARCH_PER_DAY = 150

/** How many of the site's target pages one sweep searches for, least recently
 * swept first. Each is one search per phrase template. */
export const OUTREACH_SWEEP_TARGETS_PER_RUN = 10

/**
 * The phrases a target is searched with. `{name}` is the target's label — a
 * country, for esimguide. Aimed at the practical-tips page rather than the
 * itinerary: on the first live run an itinerary was a day-by-day of sights
 * with nowhere a connectivity link could go, where a things-to-know list
 * nearly always has a line about SIM cards or data.
 */
export const OUTREACH_DEFAULT_SWEEP_TEMPLATES = [
  'things to know before visiting {name}',
  '{name} travel tips blog',
  'first time in {name} tips',
  '{name} travel guide blog',
] as const

/** Only one page: page two is a worse prospect at the same credit. */
export const OUTREACH_SERP_PAGES = 1

/**
 * How many link gap candidates may wait in the pool at once. The gap is free to
 * read — the competitor feed already paid for it — but a competitor with
 * fourteen hundred referring domains would otherwise fill the pool in one run.
 * A ceiling on what is waiting rather than on what a run adds, so research
 * reading them is what lets the next ones in.
 */
export const OUTREACH_GAP_WAITING = 150

/** A liveness reading stands this long. */
export const URL_LIVENESS_TTL_DAYS = 7

/**
 * When the follow-ups go, counted in days from the last send. Most replies land
 * on a follow-up rather than the first email, and a third is where a sequence
 * stops being outreach.
 */
export const OUTREACH_FOLLOWUP_DAYS = [5] as const
export const OUTREACH_MAX_SEQUENCE = 1

/**
 * How long after a send a new link from that domain is read as this campaign's.
 * Attribution and never a causal claim.
 */
export const OUTREACH_WON_WINDOW_DAYS = 90

/**
 * The band a prospect's domain rank has to fall in, on DataForSEO's 0-1000
 * scale. Set from esimguide.com's live sweep: the travel blogs worth writing to
 * sit between 176 and 328, where facebook answers 1000 and reddit 801. The
 * ceiling sits above 600 for the university and programme pages that link for
 * free, whose domains outrank any blog. A null rank is not a small rank and
 * never excludes anything.
 */
export const OUTREACH_MIN_DOMAIN_RANK = 100
export const OUTREACH_MAX_DOMAIN_RANK = 750

/**
 * The link gap's spam ceiling. Linking to every competitor at once is exactly
 * what a link farm does: on esimguide.com the domains linking to four and six
 * of six were URL shorteners and paid-link blogs scoring 50 and 60. At 20 and
 * under it is publishers, deal forums and review sites.
 */
export const OUTREACH_GAP_MAX_SPAM_SCORE = 20

/**
 * The authority the pull reads nearest first. The travel blogs worth writing to
 * sat between 176 and 328 on the live data; reading the strongest domains first
 * spent the first reads on government advisories and national magazines.
 */
export const OUTREACH_TARGET_DOMAIN_RANK = 260

/** How long a closed prospect stays live before the sweep archives it, which is
 * also how long it is before the same domain can be pitched again. */
export const OUTREACH_ARCHIVE_AFTER_DAYS = 180

/** UTC until there is a timezone column. */
export const OUTREACH_SEND_HOURS = { start: 9, end: 17 } as const

/** Above this a site pauses itself. */
export const OUTREACH_BOUNCE_PAUSE_RATE = 0.05

/**
 * Real results that are not addressable by email. They are dropped at research
 * rather than at discovery, so the pool still says they were seen.
 */
export const OUTREACH_MANUAL_PLATFORMS = [
  'reddit.com',
  'quora.com',
  'medium.com',
  'youtube.com',
  'facebook.com',
  'linkedin.com',
  'pinterest.com',
  'x.com',
  'twitter.com',
  'instagram.com',
  'tiktok.com',
  'wikipedia.org',
  'github.com',
  'stackexchange.com',
  'stackoverflow.com',
  'tripadvisor.com',
  'substack.com',
] as const

export function isManualOnlyHost(host: string): boolean {
  const value = host
    .trim()
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.$/, '')
  return OUTREACH_MANUAL_PLATFORMS.some(
    (platform) => value === platform || value.endsWith(`.${platform}`),
  )
}

/** Where an address is looked for, ordered by how likely the page is to carry a
 * human's address rather than a form. */
export const OUTREACH_CONTACT_PATHS = [
  '/contact',
  '/contact-us',
  '/about',
  '/about-us',
  // Where a travel blogger keeps the address a brand writes to.
  '/work-with-me',
  '/work-with-us',
  '/contact-me',
  '/about-me',
  '/write-for-us',
  '/advertise',
] as const

/**
 * A target pattern is a path with one `*`, matching one segment's worth of
 * characters: `/*-esim` matches `/japan-esim` and captures `japan`. Several are
 * allowed, one per line, for a site whose countries and regions differ in shape.
 * Deliberately not a regex — it is typed into a settings field by a person.
 */
export function matchTargetPattern(pattern: string, path: string): string | null {
  const trimmed = pattern.trim()
  if (!trimmed.includes('*')) return trimmed === path ? '' : null

  const [before = '', after = ''] = trimmed.split('*', 2)
  if (!path.startsWith(before) || !path.endsWith(after)) return null
  if (path.length < before.length + after.length + 1) return null

  const captured = path.slice(before.length, path.length - after.length)
  return captured.includes('/') ? null : captured
}

const LOWER_WORDS = new Set(['and', 'of', 'the', 'de', 'la'])

/** `bosnia-and-herzegovina` -> `Bosnia and Herzegovina`. */
export function targetLabel(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word, index) =>
      index > 0 && LOWER_WORDS.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ')
}
